import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker, Input, Popconfirm, Spin, message, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { AimOutlined } from '@ant-design/icons';
import { Shield, Users } from 'lucide-react';
import dayjs, { type Dayjs } from 'dayjs';
import { calendarApi, permissionConfigApi } from '../api/services';
import MonitorNodeConfig from './MonitorNodeConfig';
import { hasEditableOrSystemAdminPermission } from '../utils/permissionCodes';
import type {
  CalendarDayVO,
  PermissionLevel,
  RoleOptionVO,
  RolePermissionConfigVO,
  UserOptionVO,
  UserPermissionConfigVO,
} from '../api/types';
import {
  IpcButton,
  IpcFormField,
  IpcModal,
  IpcModuleTabs,
  IpcStatusBadge,
  IpcTable,
  type IpcStatusBadgeVariant,
} from '../components/ui';
import './CalendarConfig.css';

const weekDayTextMap: Record<number, string> = {
  1: '周一',
  2: '周二',
  3: '周三',
  4: '周四',
  5: '周五',
  6: '周六',
  7: '周日',
};

const pad2 = (value: number) => String(value).padStart(2, '0');

const parseDateToken = (token: string, fallbackYear: number): string | null => {
  const normalized = token.trim();
  if (!normalized) return null;

  const fullDateMatch = normalized.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (fullDateMatch) {
    const [, y, m, d] = fullDateMatch;
    const year = Number(y);
    const month = Number(m);
    const day = Number(d);
    const dt = new Date(year, month - 1, day);
    if (dt.getFullYear() === year && dt.getMonth() + 1 === month && dt.getDate() === day) {
      return `${year}-${pad2(month)}-${pad2(day)}`;
    }
    return null;
  }

  const compactDateMatch = normalized.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compactDateMatch) {
    const [, y, m, d] = compactDateMatch;
    const year = Number(y);
    const month = Number(m);
    const day = Number(d);
    const dt = new Date(year, month - 1, day);
    if (dt.getFullYear() === year && dt.getMonth() + 1 === month && dt.getDate() === day) {
      return `${year}-${pad2(month)}-${pad2(day)}`;
    }
    return null;
  }

  const monthDayMatch = normalized.match(/^(\d{1,2})[-/.](\d{1,2})$/);
  if (monthDayMatch) {
    const [, m, d] = monthDayMatch;
    const month = Number(m);
    const day = Number(d);
    const dt = new Date(fallbackYear, month - 1, day);
    if (dt.getFullYear() === fallbackYear && dt.getMonth() + 1 === month && dt.getDate() === day) {
      return `${fallbackYear}-${pad2(month)}-${pad2(day)}`;
    }
    return null;
  }

  return null;
};

const parseDateInput = (input: string, fallbackYear: number): { parsed: Set<string>; invalid: string[] } => {
  const tokens = input
    .split(/[,\n\r，]+/)
    .map(part => part.trim())
    .filter(Boolean);

  const parsed = new Set<string>();
  const invalid: string[] = [];

  tokens.forEach((token) => {
    const parsedDate = parseDateToken(token, fallbackYear);
    if (parsedDate) {
      parsed.add(parsedDate);
      return;
    }
    invalid.push(token);
  });

  return { parsed, invalid };
};

const permissionLabelMap: Record<PermissionLevel, string> = {
  system_admin: '系统管理',
  editable: '可编辑',
  viewable: '可查看',
};

const permissionOptions = Object.entries(permissionLabelMap).map(([value, label]) => ({
  value: value as PermissionLevel,
  label,
}));

function permissionBadgeVariant(level: PermissionLevel): IpcStatusBadgeVariant {
  if (level === 'system_admin') return 'destructive';
  if (level === 'editable') return 'primary';
  return 'muted';
}

const CALENDAR_CONFIG_TABS = ['settings', 'permission', 'monitor-node-config'] as const;
type CalendarConfigTabKey = (typeof CALENDAR_CONFIG_TABS)[number];

function isCalendarConfigTabKey(value: string | undefined): value is CalendarConfigTabKey {
  return value != null && (CALENDAR_CONFIG_TABS as readonly string[]).includes(value);
}

const CalendarConfig: FC = () => {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const [year, setYear] = useState<number>(dayjs().year());
  const [month, setMonth] = useState<number | undefined>(undefined);
  const [rows, setRows] = useState<CalendarDayVO[]>([]);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [isEditing, setIsEditing] = useState(false);
  const [pendingLocateToCurrentMonth, setPendingLocateToCurrentMonth] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [holidayInput, setHolidayInput] = useState('');
  const [workdayInput, setWorkdayInput] = useState('');
  const [roleOptions, setRoleOptions] = useState<RoleOptionVO[]>([]);
  const [userOptions, setUserOptions] = useState<UserOptionVO[]>([]);
  const [canManagePermission, setCanManagePermission] = useState(false);
  const [permissionChecked, setPermissionChecked] = useState(false);
  /** 日历工作日/导入/初始化：可编辑或系统管理 */
  const [canMutateCalendar, setCanMutateCalendar] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionSaving, setPermissionSaving] = useState(false);
  const [selectedRoleCode, setSelectedRoleCode] = useState<string>();
  const [selectedRolePermission, setSelectedRolePermission] = useState<PermissionLevel>('viewable');
  const [selectedUserId, setSelectedUserId] = useState<number>();
  const [selectedUserPermission, setSelectedUserPermission] = useState<PermissionLevel>('viewable');
  const [rolePermissions, setRolePermissions] = useState<RolePermissionConfigVO[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermissionConfigVO[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const list = await calendarApi.getList({ year, month });
      setRows(list);
    } catch (e) {
      console.error('加载日历失败', e);
      message.error(e instanceof Error ? e.message : '加载日历失败');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!canMutateCalendar) setIsEditing(false);
  }, [canMutateCalendar]);

  useEffect(() => {
    if (!canMutateCalendar) setImportVisible(false);
  }, [canMutateCalendar]);

  useEffect(() => {
    if (!pendingLocateToCurrentMonth || loading) return;
    const currentMonth = dayjs().month() + 1;
    const target = document.getElementById(`month-card-${currentMonth}`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPendingLocateToCurrentMonth(false);
  }, [pendingLocateToCurrentMonth, loading, year, month, rows]);

  useEffect(() => {
    const loadPermissionData = async () => {
      setPermissionLoading(true);
      try {
        const permissionCodes = await permissionConfigApi.getMyPermissionCodes();
        const normalized = (permissionCodes ?? []).map(code => code?.trim().toLowerCase());
        const canManage = normalized.some(c => c === 'system_admin');
        setCanManagePermission(canManage);
        setCanMutateCalendar(hasEditableOrSystemAdminPermission(permissionCodes));
        if (!canManage) return;

        const [roles, users, config] = await Promise.all([
          permissionConfigApi.getRoles(),
          permissionConfigApi.getUsers(),
          permissionConfigApi.getConfig(),
        ]);
        setRoleOptions(roles);
        setUserOptions(users);
        setRolePermissions(config.rolePermissions ?? []);
        setUserPermissions(config.userPermissions ?? []);
      } catch (e) {
        console.error('加载权限配置基础数据失败', e);
        message.error(e instanceof Error ? e.message : '加载权限配置数据失败');
      } finally {
        setPermissionLoading(false);
        setPermissionChecked(true);
      }
    };
    void loadPermissionData();
  }, []);

  const activeTab: CalendarConfigTabKey = isCalendarConfigTabKey(tabParam) ? tabParam : 'settings';

  useEffect(() => {
    if (!isCalendarConfigTabKey(tabParam)) {
      navigate('/calendar-config/settings', { replace: true });
      return;
    }
    if (tabParam === 'permission' && permissionChecked && !canManagePermission) {
      navigate('/calendar-config/settings', { replace: true });
    }
  }, [tabParam, permissionChecked, canManagePermission, navigate]);

  const patchRow = async (row: CalendarDayVO, patch: Partial<CalendarDayVO>) => {
    if (!canMutateCalendar) return;
    const nextRow = { ...row, ...patch };
    setRows(prev => prev.map(r => (r.id === row.id ? nextRow : r)));
    setSavingIds(prev => new Set(prev).add(row.id));
    try {
      const updated = await calendarApi.update(row.id, {
        isWorkingDay: (nextRow.is_working_day as 0 | 1) ?? 1,
        remark: nextRow.remark ?? '',
      });
      setRows(prev => prev.map(r => (r.id === row.id ? { ...r, ...updated } : r)));
    } catch (e) {
      console.error('保存日历失败', e);
      message.error(e instanceof Error ? e.message : '保存失败');
      setRows(prev => prev.map(r => (r.id === row.id ? row : r)));
    } finally {
      setSavingIds(prev => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const handleInitYear = async () => {
    if (!canMutateCalendar) return;
    setInitializing(true);
    try {
      await calendarApi.initYear({ year });
      message.success(`${year}年日历初始化成功`);
      await loadData();
    } catch (e) {
      console.error('初始化日历失败', e);
      message.error(e instanceof Error ? e.message : '初始化失败');
    } finally {
      setInitializing(false);
    }
  };

  const dayMap = useMemo(() => {
    const map = new Map<string, CalendarDayVO>();
    rows.forEach(r => map.set(dayjs(r.calendar_date).format('YYYY-MM-DD'), r));
    return map;
  }, [rows]);

  const monthList = useMemo(() => (month ? [month] : Array.from({ length: 12 }).map((_, i) => i + 1)), [month]);
  const isCurrentYearInitialized = useMemo(() => {
    return year === dayjs().year() && rows.length > 0;
  }, [year, rows]);

  const handleLocateCurrentMonth = () => {
    const currentYear = dayjs().year();
    setPendingLocateToCurrentMonth(true);
    setYear(currentYear);
    setMonth(undefined);
  };

  const handleImport = async () => {
    if (!canMutateCalendar) return;
    const { parsed: holidayDates, invalid: invalidHoliday } = parseDateInput(holidayInput, year);
    const { parsed: workdayDates, invalid: invalidWorkday } = parseDateInput(workdayInput, year);
    const invalid = [...invalidHoliday, ...invalidWorkday];

    if (invalid.length > 0) {
      message.error(`存在无法识别的日期：${invalid.slice(0, 5).join('、')}${invalid.length > 5 ? '...' : ''}`);
      return;
    }

    if (holidayDates.size === 0 && workdayDates.size === 0) {
      message.warning('请输入要导入的日期');
      return;
    }

    const conflictDates = Array.from(holidayDates).filter(d => workdayDates.has(d));
    if (conflictDates.length > 0) {
      message.error(`以下日期同时出现在两类中：${conflictDates.slice(0, 5).join('、')}${conflictDates.length > 5 ? '...' : ''}`);
      return;
    }

    setImporting(true);
    try {
      const yearRows = month ? await calendarApi.getList({ year }) : rows;
      const rowMap = new Map<string, CalendarDayVO>();
      yearRows.forEach((r) => {
        rowMap.set(dayjs(r.calendar_date).format('YYYY-MM-DD'), r);
      });

      const updates: Array<{ id: number; isWorkingDay: 0 | 1; remark: string }> = [];
      let notFoundCount = 0;

      holidayDates.forEach((dateStr) => {
        const row = rowMap.get(dateStr);
        if (!row) {
          notFoundCount += 1;
          return;
        }
        if (row.is_working_day !== 1) {
          updates.push({ id: row.id, isWorkingDay: 1, remark: row.remark ?? '' });
        }
      });

      workdayDates.forEach((dateStr) => {
        const row = rowMap.get(dateStr);
        if (!row) {
          notFoundCount += 1;
          return;
        }
        if (row.is_working_day !== 0) {
          updates.push({ id: row.id, isWorkingDay: 0, remark: row.remark ?? '' });
        }
      });

      if (updates.length === 0) {
        message.info(notFoundCount > 0 ? '没有可更新的日期（部分日期不在当前年份）' : '没有需要更新的日期');
        return;
      }

      await Promise.all(updates.map(item => calendarApi.update(item.id, {
        isWorkingDay: item.isWorkingDay,
        remark: item.remark,
      })));

      message.success(`导入完成，已更新 ${updates.length} 天${notFoundCount > 0 ? `，${notFoundCount} 天不在当前年份` : ''}`);
      setImportVisible(false);
      setHolidayInput('');
      setWorkdayInput('');
      await loadData();
    } catch (e) {
      console.error('导入日历失败', e);
      message.error(e instanceof Error ? e.message : '导入失败');
    } finally {
      setImporting(false);
    }
  };

  const handleAddRolePermission = () => {
    if (!selectedRoleCode) {
      message.warning('请选择角色');
      return;
    }
    void upsertRolePermission(selectedRoleCode, selectedRolePermission);
  };

  const handleAddUserPermission = () => {
    if (!selectedUserId) {
      message.warning('请选择用户');
      return;
    }
    void upsertUserPermission(selectedUserId, selectedUserPermission);
  };

  const upsertRolePermission = async (roleCode: string, permission: PermissionLevel) => {
    setPermissionSaving(true);
    try {
      await permissionConfigApi.upsertRolePermission({
        role_code: roleCode,
        permission,
      });
      const nextRolePermissions = rolePermissions.filter(item => item.role_code !== roleCode);
      nextRolePermissions.push({ role_code: roleCode, permission });
      setRolePermissions(nextRolePermissions);
      message.success('角色权限已生效');
    } catch (e) {
      console.error('保存权限配置失败', e);
      message.error(e instanceof Error ? e.message : '保存权限配置失败');
    } finally {
      setPermissionSaving(false);
    }
  };

  const upsertUserPermission = async (userId: number, permission: PermissionLevel) => {
    setPermissionSaving(true);
    try {
      await permissionConfigApi.upsertUserPermission({
        user_id: userId,
        permission,
      });
      const nextUserPermissions = userPermissions.filter(item => item.user_id !== userId);
      nextUserPermissions.push({ user_id: userId, permission });
      setUserPermissions(nextUserPermissions);
      message.success('用户权限已生效');
    } catch (e) {
      console.error('保存权限配置失败', e);
      message.error(e instanceof Error ? e.message : '保存权限配置失败');
    } finally {
      setPermissionSaving(false);
    }
  };

  const handleDeleteRolePermission = (roleCode: string) => {
    void deleteRolePermission(roleCode);
  };

  const handleDeleteUserPermission = (userId: number) => {
    void deleteUserPermission(userId);
  };

  const deleteRolePermission = async (roleCode: string) => {
    setPermissionSaving(true);
    try {
      await permissionConfigApi.deleteRolePermission(roleCode);
      setRolePermissions(prev => prev.filter(item => item.role_code !== roleCode));
      message.success('角色权限已删除并生效');
    } catch (e) {
      console.error('删除权限配置失败', e);
      message.error(e instanceof Error ? e.message : '删除权限配置失败');
    } finally {
      setPermissionSaving(false);
    }
  };

  const deleteUserPermission = async (userId: number) => {
    setPermissionSaving(true);
    try {
      await permissionConfigApi.deleteUserPermission(userId);
      setUserPermissions(prev => prev.filter(item => item.user_id !== userId));
      message.success('用户权限已删除并生效');
    } catch (e) {
      console.error('删除权限配置失败', e);
      message.error(e instanceof Error ? e.message : '删除权限配置失败');
    } finally {
      setPermissionSaving(false);
    }
  };

  const renderMonth = (monthNo: number) => {
    const firstDate = new Date(year, monthNo - 1, 1);
    const daysInMonth = new Date(year, monthNo, 0).getDate();
    const weekStartOffset = (firstDate.getDay() + 6) % 7; // Monday first
    const cellCount = Math.ceil((weekStartOffset + daysInMonth) / 7) * 7;
    const cells = Array.from({ length: cellCount }).map((_, idx) => {
      const dayNum = idx - weekStartOffset + 1;
      if (dayNum < 1 || dayNum > daysInMonth) return null;
      const dateStr = dayjs(new Date(year, monthNo - 1, dayNum)).format('YYYY-MM-DD');
      return dayMap.get(dateStr) ?? null;
    });

    return (
      <div className="month-card" key={monthNo} id={`month-card-${monthNo}`}>
        <div className="month-title">{monthNo}月</div>
        <div className="calendar-grid week-header">
          {Object.values(weekDayTextMap).map((label, i) => (
            <div key={`${monthNo}-${label}`} className={`week-cell ${i >= 5 ? 'weekend' : ''}`}>{label}</div>
          ))}
        </div>
        <div className="calendar-grid day-grid">
          {cells.map((row, idx) => (
            <div
              key={`${monthNo}-cell-${idx}`}
              className={`day-cell ${!row ? 'empty' : ''} ${row?.is_working_day === 1 ? 'offday' : 'workday'}`}
            >
              {!row ? null : (
                <>
                  <div className="day-top">
                    <span className="day-number">{row.calendar_day}</span>
                    <button
                      type="button"
                      className={`day-status-pill ${row.is_working_day === 0 ? 'work' : 'off'}${savingIds.has(row.id) ? ' pending' : ''}${!(isEditing && canMutateCalendar) ? ' readonly' : ''}`}
                      title={!canMutateCalendar ? '无编辑权限' : isEditing ? '点击切换班 / 休' : '请先点击右上角「编辑」'}
                      disabled={!isEditing || !canMutateCalendar || savingIds.has(row.id)}
                      onClick={() => {
                        if (!isEditing || !canMutateCalendar) return;
                        const next = row.is_working_day === 0 ? 1 : 0;
                        void patchRow(row, { is_working_day: next });
                      }}
                    >
                      {row.is_working_day === 0 ? '班' : '休'}
                    </button>
                  </div>
                  <div className={`day-actions${!(isEditing && canMutateCalendar) ? ' locked' : ''}`}>
                    <Input.TextArea
                      className="day-remark-textarea"
                      value={row.remark ?? ''}
                      placeholder="备注"
                      readOnly={!isEditing || !canMutateCalendar}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      onChange={(e) => {
                        if (!isEditing || !canMutateCalendar) return;
                        const value = e.target.value;
                        setRows(prev => prev.map(r => (r.id === row.id ? { ...r, remark: value } : r)));
                      }}
                      onBlur={() => {
                        if (!isEditing || !canMutateCalendar) return;
                        void patchRow(row, { remark: row.remark ?? '' });
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const settingsContent = (
    <div className="ipc-section-stack calendar-config-settings">
      <div className="calendar-config-toolbar-sticky">
        <div className="ipc-filter-bar calendar-config-toolbar">
          <DatePicker
            picker="year"
            allowClear={false}
            placeholder="选择年份"
            value={dayjs(`${year}-01-01`)}
            onChange={(v: Dayjs | null) => setYear(v ? v.year() : dayjs().year())}
            className="ipc-filter-bar__field calendar-config-toolbar__year"
          />
          <Select
            allowClear
            placeholder="全部月份"
            value={month}
            onChange={v => setMonth(v)}
            options={Array.from({ length: 12 }).map((_, i) => ({ value: i + 1, label: `${i + 1}月` }))}
            className="ipc-filter-bar__field calendar-config-toolbar__month"
          />
          <div className="ipc-filter-bar__spacer" />
          <IpcButton
            variant="ghost"
            iconOnly
            icon={<AimOutlined />}
            title="定位到当月"
            aria-label="定位到当月"
            onClick={handleLocateCurrentMonth}
          />
          {canMutateCalendar && (
            <>
              <IpcButton variant="secondary" onClick={() => setImportVisible(true)}>
                导入
              </IpcButton>
              <IpcButton
                variant={isEditing ? 'primary' : 'secondary'}
                onClick={() => setIsEditing(v => !v)}
              >
                {isEditing ? '完成' : '编辑'}
              </IpcButton>
              {!isCurrentYearInitialized && (
                <IpcButton variant="primary" onClick={() => void handleInitYear()} loading={initializing}>
                  初始化{year}年
                </IpcButton>
              )}
            </>
          )}
        </div>
      </div>
      {loading ? (
        <div className="calendar-loading"><Spin /></div>
      ) : (
        <div className="month-list">
          {monthList.map(m => renderMonth(m))}
        </div>
      )}
    </div>
  );

  const roleNameMap = useMemo(() => {
    const map = new Map<string, string>();
    roleOptions.forEach(item => map.set(item.role_code, item.role_name));
    return map;
  }, [roleOptions]);

  const userNameMap = useMemo(() => {
    const map = new Map<number, string>();
    userOptions.forEach(item => map.set(item.id, item.user_name));
    return map;
  }, [userOptions]);

  const sortedRolePermissions = useMemo(() => {
    return [...rolePermissions].sort((a, b) => {
      const an = roleNameMap.get(a.role_code) ?? a.role_code;
      const bn = roleNameMap.get(b.role_code) ?? b.role_code;
      return an.localeCompare(bn, 'zh-CN');
    });
  }, [rolePermissions, roleNameMap]);

  const sortedUserPermissions = useMemo(() => {
    return [...userPermissions].sort((a, b) => {
      const an = userNameMap.get(a.user_id) ?? String(a.user_id);
      const bn = userNameMap.get(b.user_id) ?? String(b.user_id);
      return an.localeCompare(bn, 'zh-CN');
    });
  }, [userPermissions, userNameMap]);

  const rolePermissionColumns: ColumnsType<RolePermissionConfigVO> = useMemo(() => [
    {
      title: '角色',
      key: 'role',
      ellipsis: true,
      render: (_, row) => (
        <div className="calendar-permission-entity">
          <span className="calendar-permission-entity__name">
            {roleNameMap.get(row.role_code) ?? row.role_code}
          </span>
          <span className="calendar-permission-entity__meta">{row.role_code}</span>
        </div>
      ),
    },
    {
      title: '权限',
      dataIndex: 'permission',
      key: 'permission',
      width: 120,
      render: (value: PermissionLevel) => (
        <IpcStatusBadge variant={permissionBadgeVariant(value)}>
          {permissionLabelMap[value] ?? value}
        </IpcStatusBadge>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 88,
      align: 'right',
      sorter: false,
      render: (_, row) => (
        <Popconfirm
          title="确认删除该角色权限吗？"
          okText="确认"
          cancelText="取消"
          onConfirm={() => handleDeleteRolePermission(row.role_code)}
        >
          <IpcButton size="small" variant="link">删除</IpcButton>
        </Popconfirm>
      ),
    },
  ], [roleNameMap]);

  const userPermissionColumns: ColumnsType<UserPermissionConfigVO> = useMemo(() => [
    {
      title: '用户',
      key: 'user',
      ellipsis: true,
      render: (_, row) => (
        <div className="calendar-permission-entity">
          <span className="calendar-permission-entity__name">
            {userNameMap.get(row.user_id) ?? row.user_id}
          </span>
          <span className="calendar-permission-entity__meta">ID {row.user_id}</span>
        </div>
      ),
    },
    {
      title: '权限',
      dataIndex: 'permission',
      key: 'permission',
      width: 120,
      render: (value: PermissionLevel) => (
        <IpcStatusBadge variant={permissionBadgeVariant(value)}>
          {permissionLabelMap[value] ?? value}
        </IpcStatusBadge>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 88,
      align: 'right',
      sorter: false,
      render: (_, row) => (
        <Popconfirm
          title="确认删除该用户权限吗？"
          okText="确认"
          cancelText="取消"
          onConfirm={() => handleDeleteUserPermission(row.user_id)}
        >
          <IpcButton size="small" variant="link">删除</IpcButton>
        </Popconfirm>
      ),
    },
  ], [userNameMap]);

  const permissionContent = (
    <Spin spinning={permissionLoading || permissionSaving}>
      <div className="calendar-permission-page">
        <div className="calendar-permission-intro">
          <h2 className="calendar-permission-intro__title">权限配置</h2>
          <p className="calendar-permission-intro__desc">
            按角色或用户授予系统管理、可编辑、可查看权限；用户权限优先于角色权限。
          </p>
        </div>

        <div className="calendar-permission-grid">
          <section className="calendar-permission-panel">
            <header className="calendar-permission-panel__head">
              <div className="calendar-permission-panel__title-wrap">
                <span className="calendar-permission-panel__icon" aria-hidden>
                  <Shield size={16} />
                </span>
                <div>
                  <h3 className="calendar-permission-panel__title">角色权限</h3>
                  <p className="calendar-permission-panel__subtitle">按角色批量授权</p>
                </div>
              </div>
              <span className="calendar-permission-panel__count">{sortedRolePermissions.length} 项</span>
            </header>

            <div className="calendar-permission-add">
              <Select
                placeholder="选择角色"
                value={selectedRoleCode}
                onChange={setSelectedRoleCode}
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                options={roleOptions.map(item => ({
                  value: item.role_code,
                  label: `${item.role_name} (${item.role_code})`,
                }))}
                className="calendar-permission-add__entity"
                getPopupContainer={() => document.body}
              />
              <Select
                placeholder="权限级别"
                value={selectedRolePermission}
                onChange={setSelectedRolePermission}
                options={permissionOptions}
                className="calendar-permission-add__level"
                getPopupContainer={() => document.body}
              />
              <IpcButton variant="primary" onClick={handleAddRolePermission}>
                添加
              </IpcButton>
            </div>

            <IpcTable<RolePermissionConfigVO>
              frameless
              compact
              showHeaderIcon={false}
              sortable={false}
              rowKey="role_code"
              columns={rolePermissionColumns}
              dataSource={sortedRolePermissions}
              pagination={false}
              emptyTitle="暂无角色权限"
              emptyDescription="选择角色与权限后点击添加"
              panelClassName="calendar-permission-table-panel"
              className="calendar-permission-table"
              scroll={{ y: 360 }}
            />
          </section>

          <section className="calendar-permission-panel">
            <header className="calendar-permission-panel__head">
              <div className="calendar-permission-panel__title-wrap">
                <span className="calendar-permission-panel__icon" aria-hidden>
                  <Users size={16} />
                </span>
                <div>
                  <h3 className="calendar-permission-panel__title">用户权限</h3>
                  <p className="calendar-permission-panel__subtitle">覆盖角色权限的个人授权</p>
                </div>
              </div>
              <span className="calendar-permission-panel__count">{sortedUserPermissions.length} 项</span>
            </header>

            <div className="calendar-permission-add">
              <Select
                placeholder="选择用户"
                value={selectedUserId}
                onChange={setSelectedUserId}
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                options={userOptions.map(item => ({
                  value: item.id,
                  label: `${item.user_name} (${item.id})`,
                }))}
                className="calendar-permission-add__entity"
                getPopupContainer={() => document.body}
              />
              <Select
                placeholder="权限级别"
                value={selectedUserPermission}
                onChange={setSelectedUserPermission}
                options={permissionOptions}
                className="calendar-permission-add__level"
                getPopupContainer={() => document.body}
              />
              <IpcButton variant="primary" onClick={handleAddUserPermission}>
                添加
              </IpcButton>
            </div>

            <IpcTable<UserPermissionConfigVO>
              frameless
              compact
              showHeaderIcon={false}
              sortable={false}
              rowKey="user_id"
              columns={userPermissionColumns}
              dataSource={sortedUserPermissions}
              pagination={false}
              emptyTitle="暂无用户权限"
              emptyDescription="选择用户与权限后点击添加"
              panelClassName="calendar-permission-table-panel"
              className="calendar-permission-table"
              scroll={{ y: 360 }}
            />
          </section>
        </div>
      </div>
    </Spin>
  );

  return (
    <div className="calendar-config-page ipc-page">
      <IpcModuleTabs
        activeKey={activeTab}
        onChange={key => {
          if (isCalendarConfigTabKey(key)) {
            navigate(`/calendar-config/${key}`);
          }
        }}
        items={[
          {
            key: 'settings',
            label: '日历配置',
            children: settingsContent,
          },
          ...(canManagePermission ? [{
            key: 'permission',
            label: '权限配置',
            children: permissionContent,
          }] : []),
          {
            key: 'monitor-node-config',
            label: '监控节点配置',
            children: <MonitorNodeConfig />,
          },
        ]}
      />
      <IpcModal
        title="导入日历日期"
        open={importVisible && canMutateCalendar}
        onOk={() => void handleImport()}
        okText="导入"
        confirmLoading={importing}
        onCancel={() => {
          if (importing) return;
          setImportVisible(false);
        }}
      >
        <div className="calendar-import-fields">
          <IpcFormField label="节假日">
            <Input.TextArea
              value={holidayInput}
              onChange={(e) => setHolidayInput(e.target.value)}
              autoSize={{ minRows: 4, maxRows: 8 }}
              placeholder="支持 YYYY-MM-DD / YYYY/MM/DD / MM-DD，逗号（中英文）或换行分隔"
            />
          </IpcFormField>
          <IpcFormField label="工作日">
            <Input.TextArea
              value={workdayInput}
              onChange={(e) => setWorkdayInput(e.target.value)}
              autoSize={{ minRows: 4, maxRows: 8 }}
              placeholder="支持 YYYY-MM-DD / YYYY/MM/DD / MM-DD，逗号（中英文）或换行分隔"
            />
          </IpcFormField>
        </div>
      </IpcModal>
    </div>
  );
};

export default CalendarConfig;
